import { useState, useRef, useEffect } from 'react'
import { useFiltersStore } from '@/store/filters'
import { useAuthStore } from '@/store/auth'
import { biApi } from '@/lib/api'
import {
  Filter,
  X,
  ChevronDown,
  Check,
  Globe,
  Radio,
  Package,
  Share2,
  Building2,
  Clock,
  RefreshCw,
} from 'lucide-react'
import clsx from 'clsx'

interface FilterOption {
  value: string | number
  label: string
  extra?: string
}

interface MultiSelectProps {
  label: string
  icon: React.ReactNode
  options: FilterOption[]
  selected: (string | number)[]
  onChange: (values: (string | number)[]) => void
  placeholder?: string
  disabled?: boolean
}

function MultiSelect({
  label,
  icon,
  options,
  selected,
  onChange,
  placeholder = 'Seleccionar...',
  disabled,
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  
  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])
  
  const toggleOption = (value: string | number) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value))
    } else {
      onChange([...selected, value])
    }
  }
  
  const clearSelection = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange([])
  }
  
  const selectedLabels = selected
    .map(v => options.find(o => o.value === v)?.label)
    .filter(Boolean)
    .join(', ')
  
  return (
    <div ref={ref} className="relative">
      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">
        {label}
      </label>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={clsx(
          'w-full flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-left transition-all',
          isOpen && 'ring-2 ring-primary-500 border-transparent',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <span className="text-gray-400">{icon}</span>
        <span className={clsx(
          'flex-1 truncate text-sm',
          selected.length > 0 ? 'text-gray-900 dark:text-white' : 'text-gray-400'
        )}>
          {selected.length > 0
            ? selected.length === 1
              ? selectedLabels
              : `${selected.length} seleccionados`
            : placeholder
          }
        </span>
        {selected.length > 0 && (
          <button
            onClick={clearSelection}
            className="p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            <X className="w-3.5 h-3.5 text-gray-400" />
          </button>
        )}
        <ChevronDown className={clsx(
          'w-4 h-4 text-gray-400 transition-transform',
          isOpen && 'rotate-180'
        )} />
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
          {options.length === 0 ? (
            <div className="p-3 text-sm text-gray-500 text-center">
              No hay opciones disponibles
            </div>
          ) : (
            options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => toggleOption(option.value)}
                className={clsx(
                  'w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors',
                  selected.includes(option.value) && 'bg-primary-50 dark:bg-primary-900/20'
                )}
              >
                <div className={clsx(
                  'w-4 h-4 rounded border flex items-center justify-center',
                  selected.includes(option.value)
                    ? 'bg-primary-600 border-primary-600'
                    : 'border-gray-300 dark:border-gray-600'
                )}>
                  {selected.includes(option.value) && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                <span className="flex-1 text-sm text-gray-900 dark:text-white truncate">
                  {option.label}
                </span>
                {option.extra && (
                  <span className="text-xs text-gray-400">{option.extra}</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export default function FilterBar({ onRefresh }: { onRefresh?: () => void }) {
  const { user } = useAuthStore()
  const { filters, options, optionsLoaded, setFilter, clearFilters, setOptions, setTimezone } = useFiltersStore()
  const [isExpanded, setIsExpanded] = useState(false)
  const [loading, setLoading] = useState(false)
  
  // Load filter options on mount
  useEffect(() => {
    if (!optionsLoaded) {
      loadFilterOptions()
    }
  }, [optionsLoaded])
  
  const loadFilterOptions = async () => {
    setLoading(true)
    try {
      const response = await biApi.getFilterOptions()
      if (response.data.success) {
        setOptions(response.data.data)
      }
    } catch (error) {
      console.error('Failed to load filter options:', error)
    } finally {
      setLoading(false)
    }
  }
  
  const hasActiveFilters = 
    filters.countries.length > 0 ||
    filters.operators.length > 0 ||
    filters.productIds.length > 0 ||
    filters.sources.length > 0 ||
    filters.customerIds.length > 0 ||
    filters.flow !== ''
  
  const activeFilterCount = [
    filters.countries,
    filters.operators,
    filters.productIds,
    filters.sources,
    filters.customerIds,
  ].filter(f => f.length > 0).length + (filters.flow ? 1 : 0)
  
  // Format options for selects
  const countryOptions = options.countries.map(c => ({
    value: c.code,
    label: `${c.flag} ${c.name}`,
    extra: c.code,
  }))
  
  // Filter operators by selected country(s) - CASCADE: País → Operador
  const filteredOperators = filters.countries.length > 0
    ? options.operators.list.filter(o => filters.countries.includes(o.country))
    : options.operators.list
  
  const operatorOptions = filteredOperators.map(o => ({
    value: o.operator,
    label: o.operator,
    extra: o.country,
  }))
  
  const isOperatorFilterDisabled = filters.countries.length === 0
  
  // Derive customers from products by country - CASCADE: País → Cliente
  const customersInSelectedCountries = filters.countries.length > 0
    ? [...new Set(
        options.products
          .filter(p => filters.countries.includes(p.country) && p.customerId)
          .map(p => p.customerId)
      )]
    : options.customers.map(c => c.id)
  
  const filteredCustomers = options.customers.filter(c => 
    customersInSelectedCountries.includes(c.id)
  )
  
  const customerOptions = filteredCustomers.map(c => ({
    value: c.id,
    label: c.name,
    extra: c.shortName || undefined,
  }))
  
  const isCustomerFilterDisabled = filters.countries.length === 0
  
  // Filter products by selected customer(s) - CASCADE: Cliente → Producto
  const filteredProducts = filters.customerIds.length > 0
    ? options.products.filter(p => p.customerId && filters.customerIds.includes(p.customerId))
    : options.products
  
  const productOptions = filteredProducts.slice(0, 100).map(p => ({
    value: p.id,
    label: p.name,
    extra: p.customerName || p.country || undefined,
  }))
  
  const isProductFilterDisabled = filters.customerIds.length === 0
  
  const sourceOptions = options.sources.map(s => ({
    value: s,
    label: s,
  }))
  
  // Cascade clear handlers
  const handleCountryChange = (values: (string | number)[]) => {
    setFilter('countries', values as string[])
    // Clear dependent filters when country changes
    if (filters.operators.length > 0) {
      const validOperators = options.operators.list
        .filter(o => values.includes(o.country))
        .map(o => o.operator)
      const newOperators = filters.operators.filter(op => validOperators.includes(op))
      if (newOperators.length !== filters.operators.length) {
        setFilter('operators', newOperators)
      }
    }
    if (filters.customerIds.length > 0) {
      const validCustomerIds = [...new Set(
        options.products
          .filter(p => values.includes(p.country) && p.customerId)
          .map(p => p.customerId!)
      )]
      const newCustomerIds = filters.customerIds.filter(id => validCustomerIds.includes(id))
      if (newCustomerIds.length !== filters.customerIds.length) {
        setFilter('customerIds', newCustomerIds)
        setFilter('productIds', []) // Also clear products
      }
    }
  }
  
  const handleCustomerChange = (values: (string | number)[]) => {
    setFilter('customerIds', values as number[])
    // Clear product selection when customer changes
    if (filters.productIds.length > 0) {
      setFilter('productIds', [])
    }
  }
  
  const timezoneOptions = options.timezones.map(tz => ({
    value: tz.value,
    label: tz.label,
  }))
  
  return (
    <div className="card mb-4">
      {/* Header - always visible */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <Filter className="w-5 h-5 text-gray-400" />
          <span className="font-medium text-gray-900 dark:text-white">Filtros</span>
          {activeFilterCount > 0 && (
            <span className="badge-primary">{activeFilterCount} activos</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onRefresh && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onRefresh()
              }}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Actualizar datos"
            >
              <RefreshCw className="w-4 h-4 text-gray-500" />
            </button>
          )}
          <ChevronDown className={clsx(
            'w-5 h-5 text-gray-400 transition-transform',
            isExpanded && 'rotate-180'
          )} />
        </div>
      </div>
      
      {/* Filters - collapsible */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-primary-500 animate-spin" />
            </div>
          ) : (
            <>
              {/* Filter grid - Order: Timezone, País, Operador, Cliente, Producto, Fuente */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                {/* Timezone */}
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">
                    Zona Horaria
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <select
                      value={filters.timezone}
                      onChange={(e) => setTimezone(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm appearance-none cursor-pointer"
                    >
                      {timezoneOptions.map(tz => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                
                {/* 1. Country filter - Primary filter */}
                <MultiSelect
                  label="País"
                  icon={<Globe className="w-4 h-4" />}
                  options={countryOptions}
                  selected={filters.countries}
                  onChange={handleCountryChange}
                  placeholder="Seleccione país"
                />
                
                {/* 2. Operator filter - Requires country */}
                <MultiSelect
                  label="Operador"
                  icon={<Radio className="w-4 h-4" />}
                  options={operatorOptions}
                  selected={filters.operators}
                  onChange={(values) => setFilter('operators', values as string[])}
                  placeholder={isOperatorFilterDisabled ? "Seleccione país primero" : "Todos los operadores"}
                  disabled={isOperatorFilterDisabled}
                />
                
                {/* 3. Customer filter - Requires country (Admin only) */}
                {user?.isAdmin && (
                  <MultiSelect
                    label="Cliente"
                    icon={<Building2 className="w-4 h-4" />}
                    options={customerOptions}
                    selected={filters.customerIds}
                    onChange={handleCustomerChange}
                    placeholder={isCustomerFilterDisabled ? "Seleccione país primero" : "Todos los clientes"}
                    disabled={isCustomerFilterDisabled}
                  />
                )}
                
                {/* 4. Product filter - Requires customer */}
                <MultiSelect
                  label="Producto"
                  icon={<Package className="w-4 h-4" />}
                  options={productOptions}
                  selected={filters.productIds}
                  onChange={(values) => setFilter('productIds', values as number[])}
                  placeholder={isProductFilterDisabled ? "Seleccione cliente primero" : "Todos los productos"}
                  disabled={isProductFilterDisabled}
                />
                
                {/* 5. Source filter - Independent */}
                <MultiSelect
                  label="Fuente de Tráfico"
                  icon={<Share2 className="w-4 h-4" />}
                  options={sourceOptions}
                  selected={filters.sources}
                  onChange={(values) => setFilter('sources', values as string[])}
                  placeholder="Todas las fuentes"
                />
                
                {/* 6. Flow filter - Independent */}
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">
                    Flujo
                  </label>
                  <select
                    value={filters.flow}
                    onChange={(e) => setFilter('flow', e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm appearance-none cursor-pointer"
                  >
                    <option value="">Todos los flujos</option>
                    <option value="sms">SMS</option>
                    <option value="sat">SAT</option>
                  </select>
                </div>
              </div>
              
              {/* Clear filters button */}
              {hasActiveFilters && (
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={clearFilters}
                    className="btn-ghost text-sm text-error-600 hover:text-error-700"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Limpiar filtros
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
